import { ShowProductsBtn } from "~/features/selectionAssistant/bearing/components/ShowProductsBtn";
import { selectionAssistantOnMount } from "~/features/search/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { selectIsInFlight, selectProductResultAmount } from "~/features/search/redux/searchSelectors";
import { SATabularView } from "~/features/selectionAssistant/bearing/components/SATabularView";
import Spinner from "~/common/components/Spinner/Spinner";
import { useOnMount } from "~/common/hooks/useOnMount";
import { NoSearchResults } from "~/features/search/components/common/NoSearchResults";
import { SAFilters } from "~/features/selectionAssistant/bearing/components/Filters/SAFilters";
import { ReduxImperialToggle } from "~/domains/userPreferences/components/UnitModeToggle";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { Box, Stack, styled } from "~/common/mui";

const ResultsContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.up("desktop-s")]: {
    marginTop: theme.spacing(3.75),
  },
  ".MuiTableRow-root.MuiTableRow-hover": {
    cursor: "initial !important",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));
import { resetSAFilters } from "~/features/selectionAssistant/bearing/redux/actions";
import { ResetAllLinkBtn } from "~/features/selectionAssistant/bearing/components/Buttons/ResetAllBtn";
import { PurchasePlaceProvider, PurchaseTypeProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";

export const ButtonsRow = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
    gap: theme.spacing(4),
  },
  [theme.breakpoints.up("desktop-s")]: {
    marginTop: 0,
    marginBottom: theme.spacing(4),
  },
}));

export const BearingSelectionAssistant = () => {
  const dispatch = useDispatch();

  useOnMount(() => {
    dispatch(selectionAssistantOnMount());
  });

  const inFlight = useSelector(selectIsInFlight);
  const totalNumberOfResults = useSelector(selectProductResultAmount);

  const { t } = useTranslation();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  return (
    <PurchasePlaceProvider value={"BSA"}>
      <PurchaseTypeProvider value={"bsa"}>
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PageTitle>{t("page.selectionAssistant.title")}</PageTitle>

          {!inFlight && (
            <ButtonsRow>
              <ShowProductsBtn resultAmount={totalNumberOfResults} />
              <ResetAllLinkBtn onClick={() => dispatch(resetSAFilters())} />
            </ButtonsRow>
          )}

          {!isDesktop && (
            <Box sx={{ mb: 3 }}>
              <ReduxImperialToggle page={"SA"} />
            </Box>
          )}

          <SAFilters />

          <ResultsContainer id="results" className={"scha__sa__results"}>
            {inFlight && <Spinner />}

            {!inFlight && (
              <>
                {totalNumberOfResults === 0 && <NoSearchResults mode={"short"} variant="filter" />}
                {totalNumberOfResults > 0 && (
                  <SATabularView totalNumberOfResults={totalNumberOfResults} isDesktop={isDesktop} />
                )}
              </>
            )}
          </ResultsContainer>
        </Box>
      </PurchaseTypeProvider>
    </PurchasePlaceProvider>
  );
};

export function createSelectionAssistant() {
  return <BearingSelectionAssistant />;
}
